import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  LuBuilding2,
  LuCloudUpload,
  LuExternalLink,
  LuFile,
  LuFileImage,
  LuFileSpreadsheet,
  LuFileText,
  LuFolderOpen,
  LuLoaderCircle,
  LuPresentation,
  LuTrash2,
  LuUpload,
} from 'react-icons/lu';
import { storage, db } from '../../config/firebaseConfig';
import { isArchivedAssignment } from '../../utils/assignmentHistory';
// Only global theme variables are left in this file; importing it here keeps their place in the cascade
import './FileUploadModal.module.css';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import IconButton from '../../components/ui/IconButton';
import Modal from '../../components/ui/Modal';
import { GLASS_CHIP, ICON_BUTTON } from '../../components/ui/glass';
import { Field, SelectInput } from '../../components/form/FormControls';
import { useTranslation } from '../../i18n/LanguageContext';
import { formatLeftDate } from './workers/workerOptions';

const fileVisual = (fileName) => {
  const extension = String(fileName || '').split('.').pop().toLowerCase();
  if (extension === 'pdf') return { icon: LuFileText, tone: 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300' };
  if (['doc', 'docx'].includes(extension)) return { icon: LuFileText, tone: 'tw-bg-blue-500/10 tw-text-blue-600 dark:tw-text-blue-300' };
  if (['xls', 'xlsx', 'csv'].includes(extension)) return { icon: LuFileSpreadsheet, tone: 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300' };
  if (['ppt', 'pptx'].includes(extension)) return { icon: LuPresentation, tone: 'tw-bg-orange-500/10 tw-text-orange-600 dark:tw-text-orange-300' };
  if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) return { icon: LuFileImage, tone: 'tw-bg-amber-500/10 tw-text-amber-600 dark:tw-text-amber-300' };
  return { icon: LuFile, tone: 'tw-bg-slate-500/10 tw-text-slate-600 dark:tw-text-slate-300' };
};

/**
 * Bir çalışanın projedeki sunum dosyaları, merkez bazında: dosya Storage'a
 * (projects/{pid}/employees/{eid}/centers/{centerId}/sunum/{ad}) ve çalışanın
 * o merkezdeki atama satırlarının `files` listesine yazılır. Ayrıldığı
 * merkezler de seçilebilir; oradaki eski dosyalar korunur.
 */
const EmployeeFileUploadModal = ({ onClose, project, employee }) => {
  const { t, language, locale } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [confirmingKey, setConfirmingKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [selectedCenterId, setSelectedCenterId] = useState('');
  const fileInputRef = useRef(null);
  const label = (key, vars) => t(`projectManagement.workerFiles.${key}`, vars);
  const fileText = (key, vars) => t(`projectManagement.files.${key}`, vars);

  // Merkez başına tek seçenek; o merkezde aktif satırı kalmadıysa "Ayrıldı"
  const centerOptions = useMemo(() => {
    const byCenter = new Map();
    (employee.centers || []).forEach((center) => {
      const existing = byCenter.get(center.centerId);
      if (!existing) {
        byCenter.set(center.centerId, { ...center, left: isArchivedAssignment(center) });
      } else if (existing.left && !isArchivedAssignment(center)) {
        byCenter.set(center.centerId, { ...existing, left: false });
      }
    });
    return [...byCenter.values()];
  }, [employee]);

  useEffect(() => {
    if (employee.centers && employee.centers.length > 0) {
      // Yeni yükleme varsayılan olarak hâlâ çalıştığı bir merkeze gitsin;
      // ayrıldığı merkezler sadece eski dosyaları görmek için listede kalır.
      const firstActive = employee.centers.find(center => !isArchivedAssignment(center));
      setSelectedCenterId((firstActive || employee.centers[0]).centerId);
    }
  }, [employee]);

  const formatDate = (dateString) => (dateString
    ? new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(dateString))
    : '');

  const fetchUploadedFiles = async () => {
    try {
      // Get the project document to fetch existing files for this employee
      const projectDoc = await getDoc(doc(db, 'Projects', project.id));
      const projectData = projectDoc.data();

      if (projectData.employees) {
        // Find all entries for this employee
        const employeeEntries = projectData.employees.filter(emp => emp.id === employee.id);

        // Collect all files from all entries
        const allFiles = [];
        employeeEntries.forEach(entry => {
          if (entry.files && Array.isArray(entry.files)) {
            allFiles.push(...entry.files.map(file => ({
              ...file,
              centerId: entry.centerId,
              centerName: entry.centerName
            })));
          }
        });

        setUploadedFiles(allFiles);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUploadedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id, employee.id]);

  const handleFileUpload = async (file) => {
    setIsUploading(true);
    try {
      // Create a reference to the file in Firebase Storage
      const fileRef = ref(storage, `projects/${project.id}/employees/${employee.id}/centers/${selectedCenterId}/sunum/${file.name}`);

      // Upload the file
      await uploadBytes(fileRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(fileRef);

      // Update the project document in Firestore
      const projectRef = doc(db, 'Projects', project.id);
      const newFile = {
        type: 'sunum',
        name: file.name,
        url: downloadURL,
        uploadedAt: new Date().toISOString(),
        storagePath: `projects/${project.id}/employees/${employee.id}/centers/${selectedCenterId}/sunum/${file.name}`
      };

      // Get project document
      const projectDoc = await getDoc(projectRef);
      const projectData = projectDoc.data();

      if (projectData.employees) {
        // Update only the specific entry for this employee and center
        const updatedEmployees = projectData.employees.map(emp => {
          if (emp.id === employee.id && emp.centerId === selectedCenterId) {
            const existingFiles = emp.files || [];
            return {
              ...emp,
              files: [...existingFiles, newFile]
            };
          }
          return emp;
        });

        await updateDoc(projectRef, {
          employees: updatedEmployees
        });
      }

      // Refresh the files list
      await fetchUploadedFiles();
      toast.success(fileText('uploaded', { name: file.name }));
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(fileText('uploadError'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = async (fileToDelete, key) => {
    setDeletingKey(key);
    try {
      // Delete from Firebase Storage
      const storagePath = fileToDelete.storagePath || `projects/${project.id}/employees/${employee.id}/sunum/${fileToDelete.name}`;
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);

      // Update Firestore document
      const projectRef = doc(db, 'Projects', project.id);
      const projectDoc = await getDoc(projectRef);
      const projectData = projectDoc.data();

      if (projectData.employees) {
        // Remove the file from this employee's rows at that center (all of them
        // when the file has no center, as old files may not)
        const updatedEmployees = projectData.employees.map(emp => {
          if (emp.id === employee.id && (fileToDelete.centerId ? emp.centerId === fileToDelete.centerId : true)) {
            const existingFiles = emp.files || [];
            const updatedFiles = existingFiles.filter(file => file.url !== fileToDelete.url);
            return {
              ...emp,
              files: updatedFiles
            };
          }
          return emp;
        });

        await updateDoc(projectRef, {
          employees: updatedEmployees
        });
      }

      // Refresh the files list
      await fetchUploadedFiles();
      toast.success(fileText('deleted'));
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(fileText('deleteError', { message: error.message }));
    } finally {
      setDeletingKey(null);
      setConfirmingKey(null);
    }
  };

  const openPicker = () => {
    if (!isUploading) fileInputRef.current?.click();
  };

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(event.type === 'dragenter' || event.type === 'dragover');
  };

  const handleDrop = (event) => {
    handleDrag(event);
    if (!isUploading && event.dataTransfer.files.length > 0) handleFileUpload(event.dataTransfer.files[0]);
  };

  // Bir merkezde birden çok rol satırı varsa dosya her birine yazılır; listede bir kez
  const shownFiles = [];
  uploadedFiles.forEach((file) => {
    const key = `${file.centerId || ''}|${file.url}`;
    if (!shownFiles.some((item) => item.key === key)) shownFiles.push({ key, file });
  });
  const leftLabelOf = (center) => {
    const date = formatLeftDate(employee.centers.find((row) => row.centerId === center.centerId && isArchivedAssignment(row)), locale);
    return date ? t('projectManagement.workers.detail.leftOn', { date }) : t('projectManagement.workers.status.left');
  };
  const employeeName = `${employee.name || ''} ${employee.surname || ''}`.trim();

  const renderFiles = () => {
    if (loading) {
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-10 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
          <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
          {fileText('loading')}
        </div>
      );
    }
    if (shownFiles.length === 0) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-200 tw-px-4 tw-py-10 tw-text-center dark:tw-border-white/10">
          <LuFolderOpen aria-hidden="true" className="tw-h-7 tw-w-7 tw-text-slate-300 dark:tw-text-slate-600" />
          <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{fileText('none')}</p>
        </div>
      );
    }
    return (
      <ul className="tw-space-y-2">
        {shownFiles.map(({ key, file }) => {
          const { icon: Icon, tone } = fileVisual(file.name);
          const confirming = confirmingKey === key;
          const deleting = deletingKey === key;
          return (
            <li key={key} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-p-2.5 ${GLASS_CHIP}`}>
              <span className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl ${tone}`}>
                <Icon aria-hidden="true" className="tw-h-5 tw-w-5" />
              </span>
              <div className="tw-min-w-0 tw-flex-1">
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={file.name}
                  className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 tw-transition-colors hover:tw-text-teal-700 dark:tw-text-slate-100 dark:hover:tw-text-teal-300"
                >
                  {file.name}
                </a>
                <p className="tw-mt-1 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  <span className="tw-rounded-full tw-bg-indigo-500/10 tw-px-2 tw-py-0.5 tw-font-semibold tw-text-indigo-700 dark:tw-text-indigo-300">{label('type')}</span>
                  {file.centerName && (
                    <span className="tw-inline-flex tw-min-w-0 tw-max-w-full tw-items-center tw-gap-1">
                      <LuBuilding2 aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                      <span className="tw-truncate">{file.centerName}</span>
                    </span>
                  )}
                  {file.uploadedAt && <span className="tw-tabular-nums">{formatDate(file.uploadedAt)}</span>}
                </p>
              </div>
              {confirming ? (
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
                  <span className="tw-hidden tw-text-xs tw-font-semibold tw-text-rose-600 sm:tw-inline dark:tw-text-rose-400">{fileText('confirmDelete')}</span>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmingKey(null)} disabled={deleting}>{fileText('keep')}</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDeleteFile(file, key)} disabled={deleting}>
                    {deleting
                      ? <LuLoaderCircle aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
                      : <LuTrash2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
                    {fileText('delete')}
                  </Button>
                </div>
              ) : (
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={fileText('open')}
                    aria-label={fileText('open')}
                    className={`${ICON_BUTTON} tw-text-slate-600 hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-text-white`}
                  >
                    <LuExternalLink aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </a>
                  <IconButton
                    label={fileText('delete')}
                    tone="alert"
                    onClick={() => setConfirmingKey(key)}
                    disabled={isUploading || Boolean(deletingKey)}
                    className="disabled:tw-opacity-50"
                  >
                    <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </IconButton>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Modal
      isOpen
      onClose={() => !isUploading && onClose()}
      size="form"
      title={label('title')}
      subtitle={[employeeName, project.titleShort].filter(Boolean).join(' · ')}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuPresentation aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
      lang={language}
      footer={<Button variant="ghost" onClick={onClose} disabled={isUploading}>{t('common.close')}</Button>}
    >
      <div className="tw-grid tw-gap-5 md:tw-grid-cols-[minmax(0,17rem)_minmax(0,1fr)]">
        {/* Upload */}
        <div className="tw-space-y-3">
          {centerOptions.length > 0 && (
            <Field label={label('center')} htmlFor="employee-file-center" hint={label('centerHint')}>
              <SelectInput
                id="employee-file-center"
                value={selectedCenterId}
                current={selectedCenterId}
                onChange={(event) => setSelectedCenterId(event.target.value)}
                disabled={isUploading}
                options={centerOptions.map((center) => ({
                  value: center.centerId,
                  label: `${center.centerName || center.centerId}${center.left ? ` — ${leftLabelOf(center)}` : ''}`,
                }))}
              />
            </Field>
          )}

          <input
            ref={fileInputRef}
            type="file"
            onChange={(event) => {
              const file = event.target.files[0];
              // Cleared so the same file can be chosen again
              event.target.value = '';
              if (file) handleFileUpload(file);
            }}
            className="tw-hidden"
            disabled={isUploading}
          />

          <div
            role="button"
            tabIndex={0}
            aria-disabled={isUploading || undefined}
            onClick={openPicker}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openPicker();
              }
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-dashed tw-px-4 tw-py-7 tw-text-center tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
              dragActive
                ? 'tw-border-teal-500 tw-bg-teal-500/10'
                : 'tw-border-slate-300 tw-bg-slate-50/60 hover:tw-border-teal-400 hover:tw-bg-teal-500/[0.04] dark:tw-border-white/15 dark:tw-bg-white/[0.02] dark:hover:tw-border-teal-400/60'
            }`}
          >
            <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="md">
              {isUploading
                ? <LuLoaderCircle aria-hidden="true" className="tw-h-6 tw-w-6 tw-animate-spin" />
                : <LuCloudUpload aria-hidden="true" className="tw-h-6 tw-w-6" />}
            </GlassIcon>
            {isUploading ? (
              <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{fileText('uploading')}</p>
            ) : (
              <>
                <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{label('drop')}</p>
                <p className="tw-text-xs tw-text-slate-400">{fileText('or')}</p>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={isUploading}
                  onClick={(event) => {
                    event.stopPropagation();
                    openPicker();
                  }}
                >
                  <LuUpload aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                  {fileText('browse')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Uploaded files */}
        <div className="tw-min-w-0 tw-space-y-3">
          <p className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
            <LuFolderOpen aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {label('list', { count: shownFiles.length })}
          </p>
          <div className="tw-pr-1 md:tw-max-h-[min(24rem,52vh)] md:tw-overflow-y-auto glass-scrollbar">{renderFiles()}</div>
        </div>
      </div>
    </Modal>
  );
};

EmployeeFileUploadModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  project: PropTypes.object.isRequired,
  employee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    surname: PropTypes.string,
    centers: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

export default EmployeeFileUploadModal;
